import React from "react";
import { useTranslation } from "react-i18next";
import { cn } from "lib/utils";
import type { RoomWeekResponse } from "api/roomWeek/roomWeekApiSlice";
import {
  BlockStyle,
  buildBlocks,
  buildWindowBlocks,
  columnsFor,
  GridBlock,
  GridColumn,
  GridDay,
  GridView,
  hourRange,
  shortColumnLabels,
  slotChoice,
  SlotChoice,
  WindowBlock,
  WindowStyle,
} from "../weekGrid";

/**
 * The week grid (M3, #3036) — presentational. Days are columns; in the hall
 * view of a divided hall each day is split into one narrow column per segment,
 * and a whole-hall booking spans all of them. Double-clicking (or tapping) a
 * segment's header opens that segment's own week.
 *
 * Geometry (a block's top and height, a column's left and width) is computed
 * per booking and therefore set as inline `style` — the one thing Tailwind
 * classes cannot express. Everything visual is a token class: the tenant
 * primary for the caller's own time, grayscale for everything else.
 *
 * Two layers: the allocation windows of ADR-0027 (`buildWindowBlocks`, W2-1)
 * behind, the bookings (`buildBlocks`) in front. Window blocks never take a
 * click: an hour of the caller's own window stays bookable underneath, and an
 * hour of another organisation's is already not offered (`slotChoice`).
 */

/** Pixel height of one hour row. */
const HOUR_PX = 44;

const BLOCK_CLASSES: Record<BlockStyle, string> = {
  own: "bg-primary text-onPrimary",
  organization: "bg-primary/30 text-foreground",
  other_organization: "bg-foreground/20 text-foreground",
  occupied: "bg-foreground/10 text-foreground/70",
  // Another segment's time in the segment view — context, not a block.
  context:
    "bg-[repeating-linear-gradient(135deg,rgba(var(--color-foreground),0.07)_0_4px,transparent_4px_9px)] pointer-events-none",
};

const WINDOW_CLASSES: Record<WindowStyle, string> = {
  // The caller's organisation's time: a frame they may book in.
  window_own: "border border-dashed border-primary bg-primary/5 text-foreground",
  // Another organisation's time: shaded, not offered.
  window_other:
    "bg-[repeating-linear-gradient(45deg,rgba(var(--color-foreground),0.10)_0_4px,rgba(var(--color-foreground),0.04)_4px_9px)] text-foreground/70",
};

export interface RoomWeekGridProps {
  data: RoomWeekResponse;
  days: GridDay[];
  view: GridView;
  /** Slots starting before this are not offered (now, or the booking window's earliest). */
  notBeforeMs: number;
  /** Slots ending after this are not offered (the booking window's horizon). */
  notAfterMs: number;
  onSlotClick: (choice: SlotChoice) => void;
  onOpenSegment: (segmentId: string) => void;
  /** The room's location zone (`data.timeZone`, #3219), the one `days` were built in. */
  timeZone: string;
}

const BlockLabel: React.FC<{ block: GridBlock }> = ({ block }) => {
  const { t } = useTranslation();
  const i = block.interval;
  if (block.style === "context") return null;
  const lines: string[] = [];
  if (i.kind === "own") {
    lines.push(i.title || t("room_week.own_booking"));
    if (i.bookingNumber) lines.push(i.bookingNumber);
  } else if (i.kind === "organization") {
    lines.push(i.title || t("room_week.organization_booking"));
    if (i.organizationName) lines.push(i.organizationName);
  } else if (i.kind === "other_organization") {
    lines.push(i.organizationName || t("room_week.occupied"));
  } else {
    lines.push(t("room_week.occupied"));
  }
  return (
    <div className="overflow-hidden px-1 py-0.5 text-[11px] leading-tight">
      {lines.map((line, index) => (
        <div key={index} className={cn("truncate", index === 0 && "font-medium")}>
          {line}
        </div>
      ))}
    </div>
  );
};

const WindowLabel: React.FC<{ block: WindowBlock }> = ({ block }) => {
  const { t } = useTranslation();
  const lines =
    block.style === "window_own"
      ? [t("room_week.window_own"), block.window.organizationName ?? ""]
      : [block.window.organizationName || t("room_week.window_reserved")];
  return (
    <div className="overflow-hidden px-1 py-0.5 text-[11px] leading-tight">
      {lines.filter(Boolean).map((line, index) => (
        <div key={index} className={cn("truncate", index === 0 && "font-medium")}>
          {line}
        </div>
      ))}
    </div>
  );
};

const DayBody: React.FC<{
  data: RoomWeekResponse;
  day: GridDay;
  columns: GridColumn[];
  blocks: GridBlock[];
  windowBlocks: WindowBlock[];
  first: number;
  last: number;
  notBeforeMs: number;
  notAfterMs: number;
  onSlotClick: (choice: SlotChoice) => void;
  timeZone: string;
}> = ({ data, day, columns, blocks, windowBlocks, first, last, notBeforeMs, notAfterMs, onSlotClick, timeZone }) => {
  const { t } = useTranslation();
  const hours = Array.from({ length: last - first }, (_, k) => first + k);
  const width = 100 / Math.max(columns.length, 1);
  return (
    <div className="relative border-l border-foreground/10" style={{ height: hours.length * HOUR_PX }}>
      {columns.map((column, columnIndex) =>
        hours.map((hour) => {
          const choice = slotChoice(data, day, hour, column.id, notBeforeMs, timeZone, notAfterMs);
          const open = !!day.hours && hour >= day.hours.fromHour && hour < day.hours.toHour;
          const clickable = choice.targets.length > 0;
          return (
            <button
              key={`${column.id}-${hour}`}
              type="button"
              disabled={!clickable}
              onClick={() => onSlotClick(choice)}
              aria-label={
                clickable
                  ? t("room_week.free_slot_aria", { column: column.name, hour })
                  : undefined
              }
              className={cn(
                "absolute border-b border-foreground/5",
                columnIndex > 0 && "border-l border-l-foreground/5",
                !open && "bg-foreground/[0.06]",
                clickable && "hover:bg-primary/10 focus-visible:bg-primary/10 focus-visible:outline-none"
              )}
              style={{
                top: (hour - first) * HOUR_PX,
                height: HOUR_PX,
                left: `${columnIndex * width}%`,
                width: `${width}%`,
              }}
            />
          );
        })
      )}
      {windowBlocks.map((block, index) => {
        const startIndex = columns.findIndex((c) => c.id === block.columns[0]);
        if (startIndex < 0) return null;
        return (
          <div
            key={`window-${index}`}
            data-layer="allocation"
            className={cn(
              "pointer-events-none absolute overflow-hidden rounded-[3px]",
              WINDOW_CLASSES[block.style]
            )}
            style={{
              top: (block.topMinutes / 60) * HOUR_PX,
              height: (block.heightMinutes / 60) * HOUR_PX,
              left: `${startIndex * width}%`,
              width: `${block.columns.length * width}%`,
            }}
          >
            <WindowLabel block={block} />
          </div>
        );
      })}
      {blocks.map((block, index) => {
        const startIndex = columns.findIndex((c) => c.id === block.columns[0]);
        if (startIndex < 0) return null;
        return (
          <div
            key={index}
            title={block.style === "context" ? t("room_week.other_segment_in_use") : undefined}
            className={cn(
              "absolute overflow-hidden rounded-[3px] border border-background",
              BLOCK_CLASSES[block.style]
            )}
            style={{
              top: (block.topMinutes / 60) * HOUR_PX,
              height: (block.heightMinutes / 60) * HOUR_PX,
              left: `${startIndex * width}%`,
              width: `${block.columns.length * width}%`,
            }}
          >
            <BlockLabel block={block} />
          </div>
        );
      })}
    </div>
  );
};

const RoomWeekGrid: React.FC<RoomWeekGridProps> = ({
  data,
  days,
  view,
  notBeforeMs,
  notAfterMs,
  onSlotClick,
  onOpenSegment,
  timeZone,
}) => {
  const { t } = useTranslation();
  const columns = columnsFor(data, view);
  const blocks = buildBlocks(data, days, view, timeZone);
  const windowBlocks = buildWindowBlocks(data, days, view, timeZone);
  const { first, last } = hourRange(days);
  const hours = Array.from({ length: last - first }, (_, k) => first + k);
  const showSegmentHeaders = data.structure.kind === "divided" && view.mode === "whole";
  const columnLabels = shortColumnLabels(columns.map((c) => c.name));

  return (
    <div
      className="grid"
      style={{ gridTemplateColumns: `3rem repeat(${days.length}, minmax(0, 1fr))` }}
    >
      {/* Header row */}
      <div />
      {days.map((day) => (
        <div key={day.dateKey} className="border-l border-foreground/10 px-1 pb-1 text-center">
          <div className="text-sm font-medium text-foreground">
            {t(`common.weekdays.${day.weekday}`)} {day.dateKey.slice(8, 10)}.{day.dateKey.slice(5, 7)}.
          </div>
          {!day.hours && (
            <div className="text-[11px] text-foreground/60">{t("room_week.closed")}</div>
          )}
          {showSegmentHeaders && (
            <div className="mt-1 flex">
              {columns.map((column, columnIndex) => (
                <button
                  key={column.id}
                  type="button"
                  onDoubleClick={() => onOpenSegment(column.id)}
                  // A phone has no double-click: a tap on the header opens it.
                  onClick={(event) => {
                    if (event.detail === 0 || window.matchMedia?.("(pointer: coarse)").matches) {
                      onOpenSegment(column.id);
                    }
                  }}
                  title={t("room_week.open_segment_hint", { segment: column.name })}
                  aria-label={t("room_week.open_segment_hint", { segment: column.name })}
                  className="min-w-0 flex-1 truncate rounded-[3px] px-0.5 text-[11px] text-foreground/70 hover:bg-foreground/5"
                >
                  {columnLabels[columnIndex]}
                </button>
              ))}
            </div>
          )}
        </div>
      ))}

      {/* Time gutter + day bodies */}
      <div className="relative" style={{ height: hours.length * HOUR_PX }}>
        {hours.map((hour) => (
          <div
            key={hour}
            className="absolute right-1 text-[11px] leading-none text-foreground/60"
            style={{ top: (hour - first) * HOUR_PX + 2 }}
          >
            {`${hour}:00`}
          </div>
        ))}
      </div>
      {days.map((day) => (
        <DayBody
          key={day.dateKey}
          data={data}
          day={day}
          columns={columns}
          blocks={blocks.filter((b) => b.dayKey === day.dateKey)}
          windowBlocks={windowBlocks.filter((b) => b.dayKey === day.dateKey)}
          first={first}
          last={last}
          notBeforeMs={notBeforeMs}
          notAfterMs={notAfterMs}
          onSlotClick={onSlotClick}
          timeZone={timeZone}
        />
      ))}
    </div>
  );
};

export default RoomWeekGrid;
